<template>
  <v-col
    class="pa-0"
  >
    <!-- Breadcrumbs with Action Buttons -->
    <v-row class="ma-0 align-center">
      <v-col class="col-auto py-0">
        <bread-crumbs />
      </v-col>

      <v-spacer />

      <!-- BurtHub and BurtForecast Buttons -->
      <v-col
        v-if="!showDashboard"
        class="col-auto py-0"
      >
        <div
          class="d-flex align-center"
          style="gap: 8px;"
        >
          <v-btn
            v-if="hasAccessToBurtForecastLink"
            class="purple"
            style="background-color: #8c46ff !important; border-color: #8c46ff !important;"
            aria-label="Open BurtForecast in a new tab"
            dark
            small
            title="Open BurtForecast in a new tab"
            @click="goToBurtForecast"
          >
            <font-awesome-icon :icon="['far', 'up-right-from-square']" />
            <span class="ml-2 d-none d-md-inline">BurtForecast</span>
          </v-btn>

          <v-btn
            v-if="hasAccessToBurtHubLink"
            class="purple"
            style="background-color: #8c46ff !important; border-color: #8c46ff !important;"
            aria-label="Open BurtHub in a new tab"
            dark
            small
            title="Open BurtHub in a new tab"
            @click="goToBurtHub"
          >
            <font-awesome-icon :icon="['far', 'up-right-from-square']" />
            <span class="ml-2 d-none d-md-inline">BurtHub</span>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Compact Header Section with Inline Tabs (show for both dashboard and report view) -->
    <div
      class="pacing-view-header-compact mb-3"
    >
      <v-toolbar
        flat
        class="header-toolbar px-0 py-0"
        height="56"
      >
        <!-- Title Icon -->
        <div class="header-icon-wrapper mr-3 ml-3">
          <font-awesome-icon
            :icon="['fas', 'chart-line']"
            size="lg"
            class="primary--text"
          />
        </div>

        <!-- Inline Tabs -->
        <v-tabs
          v-if="showPacingTabBar"
          v-model="activeTab"
          color="primary"
          slider-color="primary"
          show-arrows
          class="pacing-view-tabs-inline flex-grow-1"
          height="56"
        >
          <v-tab
            v-for="tab in tabsWithCounts"
            :key="tab.id"
            class="tab-compact"
          >
            <v-tooltip
              v-if="tab.tooltip"
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  class="d-flex align-center"
                  v-on="on"
                >
                  <font-awesome-icon
                    v-if="tab.icon"
                    :icon="tab.icon"
                    class="mr-2"
                    size="sm"
                  />
                  {{ tab.label }}
                </span>
              </template>
              <span>{{ tab.tooltip }}</span>
            </v-tooltip>
            <template v-else>
              <font-awesome-icon
                v-if="tab.icon"
                :icon="tab.icon"
                class="mr-2"
                size="sm"
              />
              {{ tab.label }}
            </template>
            <v-chip
              v-if="tab.count !== undefined"
              x-small
              outlined
              class="ml-2 tab-count-chip"
              :color="tab.chipColor || 'primary'"
            >
              {{ tab.count }}
            </v-chip>
          </v-tab>
        </v-tabs>
        <div
          v-else-if="!showDashboard"
          class="flex-grow-1"
        />
      </v-toolbar>

      <!-- Report Summary Cards Row (alternate to the tab bar, report mode only) -->
      <v-row
        v-if="!showDashboard && pacingReportLayout === 'cards'"
        class="mx-0 pacing-summary-cards"
      >
        <v-col
          v-for="(tab, index) in tabsWithCounts"
          :key="tab.id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
          xl="2"
        >
          <v-card
            :class="[
              'pa-3',
              tab.chipColor || 'primary',
              `${tab.chipColor === 'coolgray' ? 'coolgray' : 'white'}--text`,
              'text-center',
              'cursor-pointer',
              'summary-card-with-info',
              { 'summary-active': activeTab === index },
            ]"
            height="100%"
            @click="activeTab = index"
          >
            <v-card-title class="pa-0 subtitle-2 font-weight-bold justify-center">
              <font-awesome-icon
                v-if="tab.icon"
                :icon="tab.icon"
                class="mr-2"
                size="sm"
              />
              {{ tab.label }}
            </v-card-title>
            <v-card-text
              v-if="tab.count !== undefined"
              class="pa-0 pt-2 headline font-weight-bold text-center"
            >
              {{ tab.count }}
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-divider />

      <!-- Compact Controls Bar -->
      <div class="controls-bar">
        <v-row class="align-center ma-0 pa-3 pl-2">
          <!-- Search Field -->
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="py-1"
          >
            <search-field
              v-model="searchText"
              :placeholder="showDashboard ? 'Search across all networks ...' : 'Search text fields...'"
            />
          </v-col>

          <!-- Dashboard View Toolbar Buttons -->
          <template v-if="showDashboard">
            <!-- Data Tools (Filter, Sort, Group) — self-managed, no tools store -->
            <v-col
              cols="auto"
              class="py-1"
            >
              <tools-toolbar
                self-managed
                :items="assignedNetworksForCards"
                :sort-options="dashboardSortOptions"
                :group-options="dashboardGroupOptions"
                :table-columns="dashboardColumns"
                :current-required-columns="dashboardRequiredColumns"
                view-context="Card Properties"
                @on-sorting-change="onDashboardSortingChange"
                @on-grouping-change="onDashboardGroupingChange"
                @on-columns-change="onDashboardColumnsChange"
              />
            </v-col>

            <v-spacer />

            <!-- Assign Clients Button (Internal Dev Only) -->
            <v-col
              v-if="isUserInternalDev"
              cols="auto"
              class="py-1"
            >
              <v-btn
                color="primary"
                outlined
                class="text-none"
                @click="showAssignClientsDialog = true"
              >
                <font-awesome-icon
                  :icon="['fas', 'user-plus']"
                  size="sm"
                  :class="{ 'mr-1': $vuetify.breakpoint.mdAndUp }"
                />
                <span class="d-none d-md-inline">Assign Clients</span>
              </v-btn>
            </v-col>
          </template>

          <!-- Report View Toolbar Buttons -->
          <template v-if="!showDashboard">
            <!-- Data Tools (Filter, Group, Sort, Columns, Jump) -->
            <v-col
              v-if="showFilterBar"
              cols="auto"
              class="py-1"
            >
              <tools-toolbar
                :items="allLineItems"
                :current-required-columns="currentRequiredColumns"
                :report-key="reportKey"
                :table-columns="tableColumns"
                @on-grouping-change="onGroupingChange"
                @on-sorting-change="onSortingChange"
              >
                <template v-slot:filter-controls>
                  <filter-panel
                    v-model="localFilterData"
                    :fields="pacingFilterFields"
                    view-name="pacing"
                    title="Filter Line Items"
                    :show-saved-filters="false"
                  />
                </template>
              </tools-toolbar>
            </v-col>

            <!-- Refresh Button -->
            <v-col
              cols="auto"
              class="py-1"
            >
              <v-btn
                outlined
                class="text-none"
                :loading="isRefreshing"
                :disabled="isRefreshing || loadingDashboardInsights"
                @click="refreshData"
              >
                <font-awesome-icon
                  :icon="['fas', 'arrows-rotate']"
                  size="sm"
                  :class="{ 'mr-1': $vuetify.breakpoint.mdAndUp }"
                />
                <span class="d-none d-md-inline">Refresh</span>
              </v-btn>
            </v-col>

            <v-spacer />

            <!-- Export Button -->
            <v-col
              v-if="showExportBar"
              cols="auto"
              class="py-1"
            >
              <v-menu
                :disabled="!showExportBar"
                offset-y
                nudge-bottom="6"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="primary"
                    depressed
                    v-bind="attrs"
                    v-on="on"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'circle-down']"
                      size="sm"
                      :class="{ 'mr-1': $vuetify.breakpoint.mdAndUp }"
                    />
                    <span class="d-none d-md-inline">Export</span>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item
                    v-for="item in exportOptions"
                    :key="item.key"
                    :disabled="!item.active"
                    @click="item.callback"
                  >
                    <v-list-item-title v-if="item.key !== 'xlsx'">
                      {{ item.text }}
                    </v-list-item-title>
                    <v-list-item-title v-else>
                      <export-xlsx
                        :item="item"
                        :show-export-bar="showExportBar"
                      />
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </template>
        </v-row>
      </div>
    </div>

    <!-- Jump to Group (contextual row) -->
    <jump-to-group
      :groups="showDashboard ? dashboardFilteredGroups : filteredGroups"
      class="ma-0 mb-1 ml-3"
      @scroll-to-group="scrollToGroup"
    />

    <!-- Tab-specific summaries row (only show for report view) -->
    <v-row
      v-if="false && !showDashboard && showSummaries && currentTabSummaries.length > 0"
      class="mx-4 mb-4"
    >
      <v-col>
        <summary-card-bar :summaries="currentTabSummaries" />
      </v-col>
    </v-row>

    <!-- Main Content: Dashboard with Tabs -->
    <v-row
      v-if="showDashboard"
      class="ma-0"
    >
      <v-col>
        <!-- Empty State: No assigned clients (only show on My Clients tab) -->
        <v-row
          v-if="!loadingDashboardInsights && assignedClients.length === 0 && currentTab.id === 'assigned-networks'"
          class="justify-center mt-12"
        >
          <v-col
            cols="12"
            sm="8"
            md="6"
            lg="4"
          >
            <v-card class="text-center pa-8">
              <v-icon
                size="64"
                color="grey lighten-1"
                class="mb-4"
              >
                mdi-account-group-outline
              </v-icon>
              <div class="text-h6 mb-2">
                No Assigned Clients
              </div>
              <div class="text-body-2 grey--text mb-4">
                You don't have any clients assigned to you at this time.
                <template v-if="!isUserInternalDev">
                  <br>
                  Contact your administrator if you believe this is an error.
                </template>
              </div>
              <v-btn
                v-if="isUserInternalDev"
                color="primary"
                @click="showAssignClientsDialog = true"
              >
                <font-awesome-icon
                  :icon="['fas', 'user-plus']"
                  class="mr-2"
                />
                Assign Clients
              </v-btn>
            </v-card>
          </v-col>
        </v-row>

        <!-- Client Cards (ReportList) - Show when data exists or still loading -->
        <report-list
          v-else
          :search-text="searchText"
          :is-external-user="isUserExternal"
          :tab-configs="dashboardCardTabConfigs"
          :all-pacing-data="allPacingData"
          :loading="loadingDashboardInsights"
          :loading-publishers="loadingPublishers"
          :loading-client-matching="loadingClientMatching"
          :assigned-networks="sortedNetworksForCards"
          :current-tab-id="currentTab.id"
          :visible-columns="dashboardVisibleColumns"
          report-path="/reports/pacing"
        />
      </v-col>
    </v-row>

    <!-- Main Content: Report -->
    <v-row
      v-else
      class="ma-0"
    >
      <v-col>
        <data-table
          ref="table"
          :actions="actions"
          :data-groups="filteredGroups"
          :hide-toolbar-buttons="true"
        />

        <line-item-history-modal
          v-if="isLineItemHistoryModalVisible"
          v-model="isLineItemHistoryModalVisible"
          :line-item-record="currentRecord"
        />

        <creatives-report-modal
          v-if="isCreativesReportModalVisible"
          v-model="isCreativesReportModalVisible"
          :line-item-record="currentRecord"
        />

        <tearsheet-modal
          v-if="isCreativeModalVisible"
          v-model="isCreativeModalVisible"
          :line-item-record="currentRecord"
        />

        <summary-info-modal />
      </v-col>
    </v-row>

    <!-- Assign Clients Dialog (Internal Dev Only) -->
    <assign-clients-dialog
      v-if="isUserInternalDev"
      v-model="showAssignClientsDialog"
      @assigned="handleClientsAssigned"
    />
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import api from 'src/store/api'
import filter from 'src/utils/filter'
import format from 'src/utils/format'
import gam from 'src/utils/gam'
import groupUtil from 'src/utils/group'
import logger from 'src/plugins/logger'
import transform from 'src/utils/transform'
import { fireanalytics } from 'src/store/firebase'
import columns from '../store/utils/columns'
import columnUtils from '@/tools/store/utils/columns'
import mixin from '../components/mixin'
import summaries from '../store/utils/summaries'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutsGetters } = createNamespacedHelpers('layout')
const { mapGetters: mapMessagesGetters } = createNamespacedHelpers('messages')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'Pacing',
  components: {
    assignClientsDialog: () => import('../components/AssignClientsDialog'),
    breadCrumbs: () => import('@/layout/components/BreadCrumbs'),
    creativesReportModal: () => import('../components/modals/CreativesReportModal'),
    exportXlsx: () => import('@/tools/components/export/ExportXlsx'),
    filterPanel: () => import('@/tools/components/filter/FilterPanel.vue'),
    lineItemHistoryModal: () => import('../components/modals/LineItemHistoryModal'),
    reportList: () => import('@/dashboard/components/ReportList.vue'),
    summaryCardBar: () => import('@/tools/components/summary/SummaryCardBar'),
    summaryInfoModal: () => import('@/tools/components/summary/SummaryInfoModal'),
    tearsheetModal: () => import('../components/modals/TearsheetModal'),
    toolsToolbar: () => import('@/tools/components/filter/ToolsToolbar.vue'),
    JumpToGroup: () => import('@/tools/components/filter/JumpToGroup.vue'),
    SearchField: () => import('@/tools/components/filter/SearchField.vue'),
  },
  mixins: [mixin],
  data() {
    return {
      actions: [
        {
          event: 'delivery/openTabToLineItem',
          name: 'Go to Line Item',
        },
        {
          event: 'delivery/openTabToOrder',
          name: 'Go to Order',
        },
        {
          event: 'delivery/showLineItemHistoryModal',
          name: '10-Day History Report',
        },
        {
          event: 'delivery/showCreativesReportModal',
          name: 'Creatives Report',
        },
        {
          event: 'delivery/showTearsheetModal',
          name: 'Preview & Tearsheet',
        },
      ],

      linkToBurtHub: 'https://us.burthub.com',
      linkToBurtForecast: 'https://jama-forecasting.us.burthub.com/',

      searchCollection: [],
      searchText: '',

      // Active tab index
      activeTab: 0,

      // Dashboard-specific data
      assignedClients: [],
      allNetworksData: [], // For "All Networks" tab (internal users)
      dashboardInsights: null,
      loadingDashboardInsights: false,
      isRefreshing: false,
      loadingPublishers: false, // Progressive loading: publishers load first
      loadingClientMatching: false, // Progressive loading: client matching happens in background
      showAssignClientsDialog: false,

      // Network-specific data (when navigating via query params)
      networkPacingData: [],

      // Report mode data (component-level, replaces store currentItems/currentGroups)
      reportData: [],
      loadingReportData: false,

      // Component-level grouping state
      localGroupByProperties: [],

      // Component-level sorting state
      localSortingByKeys: [],
      localSortingByDirections: [],

      // Component-level filter state (for FilterPanel)
      localFilterData: {},
      localFilterKeys: [],

      // Guard flag to prevent selectedSearch watcher from firing during created()
      isCreatedFetching: false,

      // Dashboard-level sort/group state (self-managed, no tools store)
      dashboardSortConfig: [],
      dashboardGroupByProperties: [],

      // Dashboard column visibility for card properties (self-managed)
      dashboardColumns: [
        { text: 'Billing Code', value: 'billingCode', visible: true },
        { text: 'Network', value: 'networkCode', visible: true },
        { text: 'Team ID', value: 'teamId', visible: true },
      ],

      // Tab configurations for report view
      tabs: [
        {
          id: 'all',
          label: 'All Results',
          icon: ['fas', 'list'],
          chipColor: 'coolgray',
        },
        {
          id: 'at-risk',
          label: 'At Risk',
          icon: ['fas', 'exclamation-triangle'],
          chipColor: 'coolgray',
          summaryId: 'AT_RISK',
          tooltip: 'Delivering line items with outdated status (OSI 0\u201399)',
        },
        {
          id: 'stuck-paused',
          label: 'Stuck in Paused',
          icon: ['fas', 'pause'],
          chipColor: 'coolgray',
          summaryId: 'STUCK_IN_PAUSED',
        },
        {
          id: 'stuck-ready',
          label: 'Stuck in Ready',
          icon: ['fas', 'play-pause'],
          chipColor: 'coolgray',
          summaryId: 'STUCK_IN_READY',
        },
        {
          id: 'missing-creatives',
          label: 'Missing Creatives',
          icon: ['fas', 'square-xmark'],
          chipColor: 'coolgray',
          summaryId: 'MISSING_CREATIVES',
        },
        {
          id: 'zero-impressions',
          label: 'Zero Impressions',
          icon: ['fas', 'circle-0'],
          chipColor: 'coolgray',
          summaryId: 'ZERO_IMPRESSIONS',
        },
        {
          id: 'ending-soon',
          label: 'Ending Soon',
          icon: ['fas', 'calendar-minus'],
          chipColor: 'coolgray',
          summaryId: 'ENDING_SOON',
        },
        {
          id: 'viewability',
          label: 'Viewability',
          icon: ['fas', 'eye-low-vision'],
          chipColor: 'coolgray',
          summaryId: 'VIEWABILITY',
        },
      ],

      // Dashboard tabs for cross-report insights (internal users)
      internalDashboardTabs: [
        {
          id: 'assigned-networks',
          label: 'My Clients',
          icon: ['fas', 'user-check'],
          chipColor: 'primary',
        },
        {
          id: 'all-networks',
          label: 'All Networks',
          icon: ['fas', 'globe'],
          chipColor: 'coolgray',
        },
      ],

      // Dashboard tabs for external users
      externalDashboardTabs: [
        {
          id: 'all-networks',
          label: 'Networks',
          icon: ['fas', 'list'],
          chipColor: 'primary',
        },
      ],
    }
  },
  computed: {
    ...mapAuthGetters([
      'hasAccessToBurtHubLink',
      'hasAccessToBurtForecastLink',
      'isInternalUser',
      'isUserInternalDev',
      'currentUser',
      'pacingDashboardLayout',
      'pacingReportLayout',
    ]),
    ...mapLayoutsGetters([
      'showExportBar',
      'showFilterBar',
    ]),
    ...mapToolsGetters([
      'currentGroupByProperties',
      'currentRequiredColumns',
      'reportKey',
      'showSummaries',
      'sortByDirections',
      'sortByKeys',
      'tableColumns',
    ]),
    ...mapMessagesGetters(['currentMessage']),
    ...mapToolsState({
      navigation: (state) => state.navigation,
      reportId: (state) => state.reportId,
      reportName: (state) => state.reportName,
      searchResults: (state) => state.searchResults,
      selectedSearch: (state) => state.selectedSearch,
      showRows: (state) => state.showRows,
      summaries: (state) => state.summaries,
    }),
    // Delivery store state - accessed with guards since module is lazy-loaded
    currentRecord() {
      return this.$store.state.delivery?.currentRecord
    },
    isLineItemHistoryModalVisible() {
      return this.$store.state.delivery?.isLineItemHistoryModalVisible ?? false
    },
    isCreativesReportModalVisible() {
      return this.$store.state.delivery?.isCreativesReportModalVisible ?? false
    },
    isCreativeModalVisible() {
      return this.$store.state.delivery?.isCreativeModalVisible ?? false
    },
    publisher() {
      return this.$store.state.delivery?.publisher
    },
    subId() {
      return this.$store.state.delivery?.subId
    },
    showDashboard() {
      // Dashboard mode is determined entirely by the URL — no dependency on selectedSearch.
      // /reports/pacing                         → dashboard
      // /reports/pacing?billingCode=X            → report view
      // /reports/pacing/BASS2011                 → report view
      const hasBillingCodeParam = !!this.$route.query.billingCode
      const hasPathParams = !!this.$route.params.reportId
      return !hasBillingCodeParam && !hasPathParams
    },

    // User type and scoping for dashboard insights
    isUserExternal() {
      return !this.isInternalUser
    },

    currentPersonId() {
      return this.currentUser?.personId
    },

    shouldFetchAllNetworks() {
      return this.isUserExternal
    },

    shouldFetchAssignedClientsOnly() {
      return this.isInternalUser
    },

    // Determine which tabs to show
    activeTabs() {
      if (!this.showDashboard) {
        return this.tabs // Report view tabs
      }

      // 'networks-only' / 'clients-only' only apply to internal users - external users
      // already only ever see the single "Networks" tab in externalDashboardTabs.
      if (!this.isUserExternal && this.pacingDashboardLayout === 'networks-only') {
        return [this.internalDashboardTabs.find((tab) => tab.id === 'all-networks')]
      }
      if (!this.isUserExternal && this.pacingDashboardLayout === 'clients-only') {
        return [this.internalDashboardTabs.find((tab) => tab.id === 'assigned-networks')]
      }

      // Dashboard tabs based on user type
      return this.isUserExternal ? this.externalDashboardTabs : this.internalDashboardTabs
    },

    // Whether the (shared dashboard/report) tab bar itself should render.
    // Dashboard 'networks-only' hides it (single-tab content shown directly);
    // dashboard 'clients-only' still shows it (single visible tab).
    // Report 'cards' replaces it with the summary-card row instead.
    showPacingTabBar() {
      if (this.showDashboard) return this.pacingDashboardLayout !== 'networks-only'
      return this.pacingReportLayout !== 'cards'
    },

    // Current tab configuration
    currentTab() {
      const tab = this.activeTabs[this.activeTab] || this.activeTabs[0]

      // Enrich tab with summary parameters for filtering
      if (tab.summaryId) {
        const pacingSummaries = summaries?.pacing
        const summaryConfig = pacingSummaries?.[tab.summaryId]

        if (summaryConfig && summaryConfig.parameters) {
          // Convert summary parameters to filter format
          const summaryParameters = {}
          Object.keys(summaryConfig.parameters).forEach((key) => {
            const paramValue = summaryConfig.parameters[key]
            summaryParameters[key] = typeof paramValue === 'function' ? paramValue() : paramValue
          })

          return {
            ...tab,
            summaryParameters,
          }
        }
      }

      return tab
    },

    // Get all data for current view (component-level)
    allLineItems() {
      // If navigating via query params, check both networkPacingData and reportData
      // (billingCode watcher stores in networkPacingData, selectedSearch watcher stores in reportData)
      if (this.$route.query.billingCode) {
        if (this.networkPacingData.length > 0) {
          return this.networkPacingData
        }
        if (this.reportData.length > 0) {
          return this.reportData
        }
      }

      // If navigating via path params or selectedSearch, use reportData
      if (this.reportData.length > 0) {
        return this.reportData
      }

      // In report mode (non-dashboard), return empty array
      // Dashboard mode uses dashboardInsights instead
      return []
    },

    // QuickFilterPanel computed properties (component-level filtering)
    pacingFilterFields() {
      const items = this.allLineItems || []

      // Helper to build unique sorted options from data
      const buildOptions = (key) => {
        const values = [...new Set(items.map((item) => item[key]).filter(Boolean))]
        return values.sort().map((value) => ({ text: value, value }))
      }

      return [
        {
          key: 'status',
          label: 'Status',
          icon: ['fas', 'circle-check'],
          items: buildOptions('status'),
          multiple: true,
        },
        {
          key: 'advertiserName',
          label: 'Advertiser',
          icon: ['fas', 'building'],
          items: buildOptions('advertiserName'),
          multiple: true,
        },
        {
          key: 'orderName',
          label: 'Order',
          icon: ['fas', 'file-lines'],
          items: buildOptions('orderName'),
          multiple: true,
        },
        {
          key: 'priority',
          label: 'Priority',
          icon: ['fas', 'arrow-up-1-9'],
          items: buildOptions('priority'),
          multiple: true,
        },
        {
          key: 'salespersonName',
          label: 'Salesperson',
          icon: ['fas', 'user-tie'],
          items: buildOptions('salespersonName'),
          multiple: true,
        },
        {
          key: 'traffickerName',
          label: 'Trafficker',
          icon: ['fas', 'user'],
          items: buildOptions('traffickerName'),
          multiple: true,
        },
        {
          key: 'isMissingCreatives',
          label: 'Missing Creatives',
          icon: ['fas', 'square-xmark'],
          items: [
            { text: 'Yes', value: true },
            { text: 'No', value: false },
          ],
          multiple: true,
        },
      ]
    },

    activeFilterCount() {
      return this.localFilterKeys.length
    },

    // Filter line items based on active tab
    tabItems() {
      const currentTabId = this.currentTab.id

      // If "All Networks" or "All" tab, return everything
      if (currentTabId === 'all-networks' || currentTabId === 'all') {
        return this.allLineItems
      }

      // Get the summary config for this tab
      const { summaryId } = this.currentTab
      if (!summaryId) {
        return this.allLineItems
      }

      const pacingSummaries = summaries?.pacing
      const summaryConfig = pacingSummaries?.[summaryId]

      if (!summaryConfig || !summaryConfig.parameters) {
        return this.allLineItems
      }

      // Convert summary parameters to filter format
      const filterData = {}
      const paramKeys = Object.keys(summaryConfig.parameters)

      paramKeys.forEach((key) => {
        const paramValue = summaryConfig.parameters[key]
        filterData[key] = typeof paramValue === 'function' ? paramValue() : paramValue
      })

      const filterKeys = Object.keys(filterData)

      // If no filters, return all data
      if (filterKeys.length === 0) {
        return this.allLineItems
      }

      // Apply filters to get tab-specific items
      const reportFilter = filter.report.bind(filter)
      const isItemIncluded = (item) => reportFilter(item, filterData, filterKeys)

      return this.allLineItems.filter(isItemIncluded)
    },

    // Apply user filters (from QuickFilterPanel) and search filter on top of tab filter
    filteredItems() {
      let items = this.tabItems

      // Apply user-selected filters from QuickFilterPanel (if any)
      if (this.localFilterKeys.length > 0) {
        items = items.filter((item) => this.localFilterKeys.every((key) => {
          // Check every active filter
          const filterValue = this.localFilterData[key]
          const itemValue = item[key]

          // If filter value is an array (multi-select), check if item value is in array
          if (Array.isArray(filterValue)) {
            if (filterValue.length === 0) return true
            // Handle case where item value might also be an array
            if (Array.isArray(itemValue)) {
              return filterValue.some((fv) => itemValue.includes(fv))
            }
            // Check if item value is in filter array (case-insensitive for strings)
            return filterValue.some((fv) => {
              if (typeof fv === 'string' && typeof itemValue === 'string') {
                return fv.toLowerCase() === itemValue.toLowerCase()
              }
              return fv === itemValue
            })
          }

          // Single value comparison (case-insensitive for strings)
          if (typeof filterValue === 'string' && typeof itemValue === 'string') {
            return filterValue.toLowerCase() === itemValue.toLowerCase()
          }

          return filterValue === itemValue
        }))
      }

      // Apply search text filter
      if (!this.searchText || this.searchText.trim() === '') {
        return items
      }

      const searchLower = this.searchText.toLowerCase().trim()

      return items.filter((item) => {
        // Search across key fields
        const lineItemName = (item.lineItemName || '').toLowerCase()
        const orderName = (item.orderName || '').toLowerCase()
        const advertiserName = (item.advertiserName || '').toLowerCase()
        const status = (item.status || '').toLowerCase()

        return lineItemName.includes(searchLower)
          || orderName.includes(searchLower)
          || advertiserName.includes(searchLower)
          || status.includes(searchLower)
      })
    },

    // Apply sorting to filtered items
    sortedItems() {
      const items = this.filteredItems

      if (!items || items.length === 0) {
        return items
      }

      const sortKeys = this.localSortingByKeys
      const sortDirections = this.localSortingByDirections

      // If no sorting configured, return items unchanged
      if (!sortKeys || sortKeys.length === 0) {
        return items
      }

      // Create a copy to avoid mutating original array
      const sortedItems = [...items]

      // Sort using multi-level comparison
      sortedItems.sort((a, b) => {
        let result = 0
        let i = 0

        while (result === 0 && i < sortKeys.length) {
          const key = sortKeys[i]
          // Direction is boolean: false=asc, true=desc
          const isDesc = sortDirections[i] === true

          // Get values for comparison
          let valueA = a[key]
          let valueB = b[key]

          // Handle null/undefined values (push to end)
          if (valueA == null && valueB != null) {
            result = 1
          } else if (valueA != null && valueB == null) {
            result = -1
          } else if (valueA != null && valueB != null) {
            // Convert to comparable format
            if (typeof valueA === 'string') valueA = valueA.toLowerCase()
            if (typeof valueB === 'string') valueB = valueB.toLowerCase()

            // Compare values
            if (valueA < valueB) result = -1
            else if (valueA > valueB) result = 1
          }

          // Apply sort direction if there's a difference
          if (result !== 0 && isDesc) {
            result = -result
          }

          i += 1
        }

        return result
      })

      return sortedItems
    },

    // Format filtered items as groups for DataTable component
    filteredGroups() {
      // Only return groups when showing a report (not dashboard)
      if (this.showDashboard) {
        return null
      }

      // Don't return groups if we don't have data yet
      if (!this.sortedItems || this.sortedItems.length === 0) {
        return [{
          groupName: null,
          labels: ['Line Items'],
          items: [],
          isPartOfGroup: false,
          isEmpty: true,
        }]
      }

      // Component-level grouping: Use local grouping state
      if (this.localGroupByProperties && this.localGroupByProperties.length > 0) {
        // Group the sorted items by the selected properties
        const groups = groupUtil.byProperty(this.sortedItems, this.localGroupByProperties)

        // Add isEmpty flag and convert to expected format for DataTable
        return groups.map((grp) => ({
          ...grp,
          key: grp.groupName,
          name: grp.labels.join(' :: '),
          isEmpty: grp.items.length === 0,
        }))
      }

      // Default: single ungrouped list
      return [{
        groupName: null,
        labels: ['Line Items'],
        items: this.sortedItems || [],
        isPartOfGroup: false,
        isEmpty: false,
      }]
    },

    // All pacing data for passing to dashboard cards
    allPacingData() {
      // On dashboard: use data from dashboardInsights
      if (this.showDashboard) {
        const allData = this.dashboardInsights?.pacingData || []
        const currentTabId = this.currentTab.id

        // For "At Risk" tab, filter data
        if (currentTabId === 'at-risk' && this.currentTab.summaryId) {
          const pacingSummaries = summaries?.pacing
          const summaryConfig = pacingSummaries?.[this.currentTab.summaryId]

          if (summaryConfig && summaryConfig.parameters) {
            const filterData = {}
            const paramKeys = Object.keys(summaryConfig.parameters)

            paramKeys.forEach((key) => {
              const paramValue = summaryConfig.parameters[key]
              filterData[key] = typeof paramValue === 'function' ? paramValue() : paramValue
            })

            const filterKeys = Object.keys(filterData)
            if (filterKeys.length > 0) {
              const reportFilter = filter.report.bind(filter)
              const isItemIncluded = (item) => reportFilter(item, filterData, filterKeys)
              return allData.filter(isItemIncluded)
            }
          }
        }

        return allData
      }

      // In report view: use current report data
      return this.allLineItems
    },

    // Network list for ReportList cards (dashboard mode)
    assignedNetworksForCards() {
      if (!this.showDashboard) {
        return []
      }

      const currentTabId = this.currentTab.id

      // For "My Clients" or default tab: show assigned clients
      const isTabDefaultForInternals = () => !currentTabId && this.isInternalUser

      if (currentTabId === 'assigned-networks' || isTabDefaultForInternals()) {
        if (!this.dashboardInsights?.assignedClients) return []

        // Need to enrich client data with publisher info for cards
        return this.dashboardInsights.assignedClients.map((client) => {
          // Find matching publisher from dashboardInsights if it was enriched
          const enrichedData = this.dashboardInsights.enrichedClients?.find(
            (ec) => ec.billingCode === client.billingCode,
          )

          return {
            text: client.clientName,

            billingCode: client.billingCode,
            networkCode: enrichedData?.networkCode,

            publisherName: enrichedData?.publisherName || client.clientName,
            publisherId: enrichedData?.publisherId,

            teamId: -1,
            teamCode: -1,

            // Search option properties for navigation
            isSearchOption: true,
            route: 'reports',
            reportKey: 'pacing',
            reportId: client.billingCode,
            subId: '-1',
            reportName: 'pacing',
            reportPath: 'pacing',
          }
        })
      }

      // For "All Networks" tab (internal users): show all networks with progressive loading
      if (currentTabId === 'all-networks' && this.isInternalUser) {
        if (!this.allNetworksData || this.allNetworksData.length === 0) return []

        return this.allNetworksData.map((network) => ({
          text: network.teamId === -1 ? `${network.publisherName}` : `${network.publisherName}: ${network.teamName}`,

          billingCode: network.billingCode,
          networkCode: network.networkCode,

          publisherName: network.publisherName,
          publisherId: network.publisherId,
          teamId: network.teamId,
          teamCode: network.teamCode || network.teamId,

          // Pass through loading state
          loadingClientData: network.loadingClientData,

          // Search option properties for navigation
          isSearchOption: true,
          route: 'reports',
          reportKey: 'pacing',
          reportId: network.billingCode,
          subId: String(network.teamId),
          reportName: 'pacing',
          reportPath: 'pacing',
        }))
      }

      // For external users "Networks" tab: use same progressive loading
      if (currentTabId === 'all-networks' && this.isUserExternal) {
        if (!this.allNetworksData || this.allNetworksData.length === 0) return []

        return this.allNetworksData.map((network) => ({
          text: `${network.publisherName}: ${network.teamName}`,

          billingCode: network.billingCode,
          networkCode: network.networkCode,

          publisherName: network.publisherName,
          publisherId: network.publisherId,
          teamId: network.teamId,
          teamCode: network.teamCode || network.teamId,

          // Progressive loading state
          loadingClientData: network.loadingClientData,

          // Search option properties for navigation
          isSearchOption: true,
          route: 'reports',
          reportKey: 'pacing',
          reportId: network.billingCode,
          subId: String(network.teamId),
          reportName: 'pacing',
          reportPath: 'pacing',
        }))
      }

      return []
    },

    // Dashboard sort options (self-managed, no tools store)
    dashboardSortOptions() {
      return [
        { text: 'Name', value: 'text' },
        { text: 'Billing Code', value: 'billingCode' },
        { text: 'Network Code', value: 'networkCode' },
      ]
    },

    // Dashboard group options (self-managed, no tools store)
    dashboardGroupOptions() {
      return [
        { text: 'Billing Code', value: 'billingCode' },
        { text: 'Network Code', value: 'networkCode' },
      ]
    },

    // Required columns for dashboard cards (cannot be hidden)
    dashboardRequiredColumns() {
      return []
    },

    // Visible column values for dashboard cards (passed to ReportList → EnhancedNetworkCard)
    dashboardVisibleColumns() {
      return this.dashboardColumns
        .filter((col) => col.visible !== false)
        .map((col) => col.value)
    },

    // Tab configs for dashboard cards — external users don't see "At Risk"
    dashboardCardTabConfigs() {
      // For external users, filter out any tabs irrelevant to them.
      // Example:
      // if (this.isUserExternal) {
      //   return this.tabs.filter((tab) => tab.id !== 'at-risk')
      // }

      return this.tabs
    },

    // Dashboard filtered groups (for Jump button)
    dashboardFilteredGroups() {
      if (!this.showDashboard || this.dashboardGroupByProperties.length === 0) return []

      const cards = this.assignedNetworksForCards || []
      if (cards.length === 0) return []

      const groupKey = this.dashboardGroupByProperties[0]
      const groupMap = {}

      cards.forEach((card) => {
        const value = card[groupKey] || 'Unknown'
        if (!groupMap[value]) {
          groupMap[value] = { groupName: value, labels: [value], items: [] }
        }
        groupMap[value].items.push(card)
      })

      return Object.values(groupMap).sort((a, b) => a.groupName.localeCompare(b.groupName))
    },

    // Sorted + grouped network cards for dashboard
    sortedNetworksForCards() {
      const cards = [...(this.assignedNetworksForCards || [])]
      if (cards.length === 0) return cards

      // Apply sorting
      if (this.dashboardSortConfig.length > 0) {
        cards.sort((a, b) => this.dashboardSortConfig.reduce((result, sort) => {
          if (result !== 0) return result
          const dir = sort.direction === 'desc' ? -1 : 1
          const va = a[sort.column]
          const vb = b[sort.column]

          // Handle numbers
          if (typeof va === 'number' && typeof vb === 'number') {
            return (va - vb) * dir
          }

          // Handle strings
          return String(va || '').localeCompare(String(vb || '')) * dir
        }, 0))
      }

      return cards
    },

    // Update tab counts dynamically
    tabsWithCounts() {
      // For dashboard, calculate counts from insights data
      if (this.showDashboard) {
        return this.activeTabs.map((tab) => {
          let count = 0

          if (tab.id === 'assigned-networks') {
            count = this.assignedClients.length
          } else if (tab.id === 'all-networks') {
            if (this.isUserExternal) {
              // External users: count from dashboardInsights
              count = this.dashboardInsights?.assignedClients?.length || 0
            } else {
              // Internal users: count from allNetworksData
              count = this.allNetworksData.length
            }
          } else if (tab.id === 'at-risk' && tab.summaryId) {
            // Calculate at-risk count from pacing data
            const pacingSummaries = summaries?.pacing
            const summaryConfig = pacingSummaries?.[tab.summaryId]
            if (summaryConfig) {
              const allPacingData = this.dashboardInsights?.pacingData || []
              count = this.calculateSummaryCount(allPacingData, summaryConfig)
            }
          }

          return {
            ...tab,
            count,
          }
        })
      }

      // For report view, calculate counts from line items
      const pacingSummaries = summaries?.pacing

      if (!pacingSummaries) {
        return this.tabs // Return static tabs if summaries not loaded
      }

      // Calculate counts for each tab
      return this.tabs.map((tab) => {
        if (tab.id === 'all-networks' || tab.id === 'all') {
          return {
            ...tab,
            count: this.allLineItems.length,
          }
        }

        const { summaryId } = tab

        if (!summaryId) {
          return tab
        }

        const summaryConfig = pacingSummaries[summaryId]
        const count = this.calculateSummaryCount(this.allLineItems, summaryConfig)

        return {
          ...tab,
          count,
        }
      })
    },

    // Tab-specific summaries - only show relevant metrics for current tab
    currentTabSummaries() {
      const currentTabId = this.currentTab.id
      const items = this.tabItems

      // For "All" tab, show general high-level metrics
      if (currentTabId === 'all-networks' || currentTabId === 'all') {
        if (items.length === 0) return []

        return [
          {
            id: 'total-line-items',
            title: 'Total Line Items',
            total: items.length,
            format: 'number',
            styles: {
              background: 'coolgray',
              text: 'white',
              value: 'white',
            },
          },
        ]
      }

      // For specific tabs, show contextual metrics
      if (items.length === 0) return []

      const tabSummaries = []

      // Common metric: Number of items in this tab
      tabSummaries.push({
        id: `${currentTabId}-count`,
        title: this.currentTab.label,
        total: items.length,
        format: 'number',
        styles: {
          background: this.currentTab.chipColor || 'primary',
          text: 'white',
          value: 'white',
        },
      })

      // Add context-specific metrics based on tab
      if (currentTabId === 'at-risk') {
        const avgOsi = items.reduce((sum, item) => sum + (item.osi || 0), 0) / items.length
        tabSummaries.push({
          id: 'avg-osi',
          title: 'Avg OSI',
          total: Math.round(avgOsi),
          format: 'number',
          styles: {
            background: 'warning',
            text: 'white',
            value: 'white',
          },
        })
      }

      if (currentTabId === 'ending-soon') {
        const today = new Date()
        const avgDaysLeft = items.reduce((sum, item) => {
          if (item.endDate) {
            const endDate = new Date(item.endDate)
            const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24))
            return sum + Math.max(0, daysLeft)
          }
          return sum
        }, 0) / items.length

        tabSummaries.push({
          id: 'avg-days-left',
          title: 'Avg Days Remaining',
          total: Math.round(avgDaysLeft),
          format: 'number',
          styles: {
            background: 'warning',
            text: 'white',
            value: 'white',
          },
        })
      }

      if (currentTabId === 'missing-creatives' || currentTabId === 'stuck-paused' || currentTabId === 'stuck-ready') {
        // Show unique advertisers affected
        const uniqueAdvertisers = new Set(items.map((item) => item.advertiser).filter(Boolean))
        tabSummaries.push({
          id: 'affected-advertisers',
          title: 'Advertisers Affected',
          total: uniqueAdvertisers.size,
          format: 'number',
          styles: {
            background: 'error',
            text: 'white',
            value: 'white',
          },
        })
      }

      return tabSummaries
    },

    activeGrouping() {
      // Get the current grouping from local state (synced from store)
      const groupByProperties = this.localGroupByProperties

      if (!groupByProperties || groupByProperties.length === 0) {
        return null
      }

      // Get the first grouping property and format it nicely
      const firstGrouping = groupByProperties[0]

      // Map internal keys to user-friendly names
      const groupingLabels = {
        advertiser: 'Advertiser',
        advertiserName: 'Advertiser',
        order: 'Order',
        orderName: 'Order',
        status: 'Status',
        lineItemType: 'Line Item Type',
        costType: 'Cost Type',
        startDate: 'Start Date',
        endDate: 'End Date',
      }

      return groupingLabels[firstGrouping] || firstGrouping
    },

    exportOptions() {
      const { showExportBar } = this

      return [
        {
          active: showExportBar,
          key: 'csv',
          text: 'CSV',
          callback: this.exportAsCsv,
        },
        {
          active: showExportBar,
          key: 'xlsx',
          text: 'XLSX',
          callback: this.exportAsXlsx,
        },
      ]
    },

  },
  watch: {
    // Update URL when active tab changes
    activeTab(newTab, oldTab) {
      if (newTab !== oldTab) {
        // Use the actual tab ID from activeTabs array (no hardcoded default)
        const tabId = this.activeTabs[newTab]?.id
        if (!tabId) {
          logger.console.warn(['🔄 [ROUTE-DEBUG] activeTab watcher: no tab ID found', { newTab, activeTabs: this.activeTabs }])
          return
        }

        logger.console.info(['🔄 [ROUTE-DEBUG] activeTab watcher triggered', {
          oldTab,
          newTab,
          oldTabId: this.activeTabs[oldTab]?.id,
          newTabId: tabId,
          currentRoute: this.$route.query,
          hasBillingCode: !!this.$route.query.billingCode,
          hasReportParam: !!this.$route.query.report,
          hasTabParam: !!this.$route.query.tab,
          showDashboard: this.showDashboard,
          isInternal: this.isInternalUser,
          activeTabs: this.activeTabs.map((t) => t.id),
          stackTrace: new Error().stack,
        }])

        // Skip URL update if we have billingCode query param (navigating from dashboard)
        // The URL is already correct with network and filter parameters
        if (this.$route.query.billingCode) {
          logger.console.info(['🔄 [ROUTE-DEBUG] Skipping updateUrlWithTab - has billingCode param'])
          return
        }

        // Skip URL update if on dashboard without explicit tab param in URL
        // This prevents adding tab param when navigating back to /reports/pacing
        if (this.showDashboard && !this.$route.query.tab) {
          logger.console.info(['🔄 [ROUTE-DEBUG] Skipping updateUrlWithTab - dashboard without tab param'])
          return
        }

        this.updateUrlWithTab(tabId)

        // Track tab change in analytics
        const tabType = this.showDashboard ? 'dashboard' : 'report'
        fireanalytics.event('pacing_tab_changed', {
          action: 'tab_change',
          category: 'Pacing',
          label: `Changed to ${tabId} tab in ${tabType} view`,
          properties: JSON.stringify({
            fromTab: this.activeTabs[oldTab]?.id,
            toTab: tabId,
            viewType: tabType,
          }),
        })
      }
    },

    // Sync activeTab with URL on route change
    '$route.query.tab': {
      immediate: true,
      handler(newTab) {
        logger.console.info(['🔄 [ROUTE-DEBUG] $route.query.tab watcher triggered', {
          newTab,
          currentActiveTab: this.activeTab,
          currentRoute: this.$route.query,
          hasBillingCode: !!this.$route.query.billingCode,
          showDashboard: this.showDashboard,
          stackTrace: new Error().stack,
        }])

        if (newTab) {
          // During dashboard -> report transitions, resolve tab against report tabs
          // to avoid transient activeTabs mismatches while route state settles.
          const isReportRoute = !!this.$route.query.billingCode || !!this.$route.params.reportId
          const tabsForLookup = isReportRoute ? this.tabs : this.activeTabs
          const tabIndex = tabsForLookup.findIndex((tab) => tab.id === newTab)
          if (tabIndex !== -1 && tabIndex !== this.activeTab) {
            logger.console.info(['🔄 [ROUTE-DEBUG] Setting activeTab from URL', { tabIndex, tabId: newTab }])
            this.activeTab = tabIndex
          }
        } else if (this.showDashboard) {
          // On dashboard view without tab param, don't trigger activeTab change
          // This prevents adding tab param when navigating back to /reports/pacing
          logger.console.info(['🔄 [ROUTE-DEBUG] No tab param and on dashboard - skipping activeTab update'])
        }
      },
    },

    // Fetch dashboard insights when entering dashboard mode, clear report data when leaving report mode
    showDashboard: {
      immediate: true,
      handler(isDashboard) {
        logger.console.info(['=== showDashboard watcher triggered ===', isDashboard])
        if (isDashboard) {
          // Entering dashboard mode — clear stale report data and fetch insights
          this.reportData = []
          this.networkPacingData = []
          logger.console.info(['=== About to call fetchDashboardInsights() from watcher ==='])
          this.fetchDashboardInsights()
        }
      },
    },

    // Fetch report data when billingCode query param changes (dashboard card click)
    '$route.query.billingCode': {
      async handler(newBillingCode, oldBillingCode) {
        logger.console.info(['[Pacing] billingCode query param changed', { newBillingCode, oldBillingCode }])

        // Skip if clearing billingCode (navigating back to dashboard)
        if (!newBillingCode) return

        // Skip duplicate calls
        if (oldBillingCode === newBillingCode) return

        // Resolve initial tab before async work so route query tab wins deterministically.
        const requestedTab = this.$route.query.tab
        if (requestedTab) {
          const requestedTabIndex = this.tabs.findIndex((tab) => tab.id === requestedTab)
          this.activeTab = requestedTabIndex !== -1 ? requestedTabIndex : 0
        } else {
          this.activeTab = 0
        }

        // Wait for next tick to give SearchBar time to update selectedSearch
        await this.$nextTick()

        // Get publisherId and networkCode from selectedSearch OR look up from dashboard insights
        let publisherId = this.selectedSearch?.publisherId
        let networkCode = this.selectedSearch?.networkCode
        let publisherName = this.selectedSearch?.publisherName || newBillingCode

        // If selectedSearch doesn't have publisherId yet, try to find it from dashboard data
        if (!publisherId) {
          // Check enrichedClients directly (not assignedNetworksForCards which returns [] when not in dashboard mode)
          if (this.dashboardInsights?.enrichedClients) {
            const enrichedClient = this.dashboardInsights.enrichedClients.find(
              (ec) => ec.billingCode === newBillingCode,
            )
            if (enrichedClient) {
              publisherId = enrichedClient.publisherId
              networkCode = enrichedClient.networkCode
              publisherName = enrichedClient.publisherName || enrichedClient.clientName || newBillingCode
              logger.console.info(['[Pacing] Found publisherId from enrichedClients:', publisherId, 'networkCode:', networkCode, 'publisherName:', publisherName])
            }
          }

          // Also check allNetworksData for "All Networks" tab
          if (!publisherId && this.allNetworksData) {
            const allNetwork = this.allNetworksData.find(
              (n) => n.billingCode === newBillingCode || n.reportId === newBillingCode,
            )
            if (allNetwork) {
              publisherId = allNetwork.publisherId
              networkCode = allNetwork.networkCode
              publisherName = allNetwork.publisherName || allNetwork.clientName || newBillingCode
              logger.console.info(['[Pacing] Found publisherId from allNetworksData:', publisherId, 'networkCode:', networkCode, 'publisherName:', publisherName])
            }
          }
        }

        // If still no publisherId, look up via API (needed when navigating directly to URL)
        if (!publisherId) {
          logger.console.info(['[Pacing] Looking up publisherId from API for billingCode:', newBillingCode])
          try {
            const publisherResponse = await api.getPublishersByBillingCodes(newBillingCode)
            const publisher = publisherResponse?.content?.[0]
            if (publisher) {
              publisherId = publisher.publisherId
              networkCode = publisher.networkCode
              publisherName = publisher.publisherName || publisher.clientName || newBillingCode
              logger.console.info(['[Pacing] Found publisherId from API:', publisherId, 'networkCode:', networkCode, 'publisherName:', publisherName])
            }
          } catch (err) {
            logger.console.error(['[Pacing] Error looking up publisherId from API:', err])
          }
        }

        // Final fallback - use billingCode (will likely return empty results)
        if (!publisherId) {
          logger.console.warn(['[Pacing] No publisherId found anywhere, falling back to billingCode:', newBillingCode])
          publisherId = newBillingCode
        }

        // Fetch data for this network
        const teamId = this.$route.query.teamId || this.selectedSearch?.teamId || '-1'

        // Update selectedSearch so breadcrumbs and SearchBar display the correct client name
        const selectedSearch = {
          isSearchOption: true,
          route: 'reports',
          reportKey: 'pacing',
          reportId: newBillingCode,
          billingCode: newBillingCode,
          subId: teamId,
          teamId,
          reportName: 'pacing',
          reportPath: 'pacing',
          publisherId,
          networkCode,
          publisherName,
          text: publisherName,
        }
        this.$store.commit('tools/selectedSearch', selectedSearch)

        logger.console.info([`[Pacing] Fetching data for publisherId ${publisherId}, networkCode ${networkCode}, team ${teamId}`])

        this.loadingReportData = true
        this.networkPacingData = []

        try {
          const data = await this.fetchNetworkPacingData(publisherId, teamId, networkCode)
          this.networkPacingData = data
          logger.console.info([`[Pacing] Fetched ${data.length} line items via query param watcher`])
        } catch (error) {
          logger.console.error(['[Pacing] Error fetching data via query param watcher:', error])
        } finally {
          this.loadingReportData = false
        }
      },
    },

    // Fetch report data when selectedSearch changes (non-dashboard mode)
    selectedSearch: {
      immediate: true,
      async handler(newSelection, oldSelection) {
        // Skip if still in dashboard mode or no valid selection
        if (this.showDashboard || !newSelection || newSelection.dataType === 'custom') return

        // Skip if created() is already fetching data for this route
        if (this.isCreatedFetching) return

        // Skip if billingCode query param is present - that's handled by created() or billingCode watcher
        if (this.$route.query.billingCode) return

        // Skip duplicate calls
        if (oldSelection && newSelection.reportId === oldSelection.reportId && newSelection.subId === oldSelection.subId) {
          return
        }

        // Fetch pacing data for the selected client/team (component-level)
        await this.fetchReportData()
      },
    },

    // Sync localFilterData changes to localFilterKeys (for FilterPanel)
    localFilterData: {
      deep: true,
      handler(newData) {
        this.localFilterKeys = Object.keys(newData || {})
      },
    },

    // Sync local grouping with store's currentGroupByProperties
    currentGroupByProperties: {
      immediate: true,
      handler(newGrouping) {
        if (newGrouping && Array.isArray(newGrouping)) {
          this.localGroupByProperties = [...newGrouping]
        }
      },
    },

    // Sync local sorting with store's sortByKeys
    sortByKeys: {
      immediate: true,
      handler(newSortKeys) {
        if (newSortKeys && Array.isArray(newSortKeys)) {
          this.localSortingByKeys = [...newSortKeys]
        }
      },
    },

    // Sync local sorting directions with store's sortByDirections
    sortByDirections: {
      immediate: true,
      handler(newSortDirections) {
        if (newSortDirections && Array.isArray(newSortDirections)) {
          this.localSortingByDirections = [...newSortDirections]
        }
      },
    },
  },
  async created() {
    logger.console.info(['=== Pacing created() hook called ==='])

    // Self-managed view: Set reportName in tools store
    // This is normally done by SearchBar, but we bypass that for self-managed views
    this.$store.commit('tools/reportName', 'pacing')

    // Initialize columns for ToolsDrawer (sort, filter, columns tabs)
    // This is normally done by SearchBar via setColumns action, but we bypass that for self-managed views
    const existingPacingColumns = this.$store.state.tools.columns.pacing

    if (!columnUtils.hasCompatibleColumnSchema(existingPacingColumns, columns.pacing)) {
      const storedPrefs = columnUtils.loadColumnPreferences('pacing')
      const mergedColumns = columnUtils.applyColumnPreferences(columns.pacing, storedPrefs)
      this.$store.commit('tools/columns', { pacing: mergedColumns })
    }

    // Derive valid sort keys from columns.js (sortable columns only)
    const validPacingKeys = columns.pacing
      .filter((col) => col.sortable)
      .map((col) => col.key)

    // Initialize default sorting by advertiser and order (ascending) if not set or if current sort key is invalid for this view
    const currentSortKeys = this.$store.state.tools.sortingByKeys || []
    const hasValidSortKey = currentSortKeys.length > 0 && currentSortKeys.every((key) => validPacingKeys.includes(key))

    if (!hasValidSortKey) {
      this.$store.commit('tools/sortingByKeys', ['advertiserName', 'orderName'])
      this.$store.commit('tools/sortingByDirections', ['asc', 'asc'])
    }

    // Initialize tab from URL on component creation
    const tabParam = this.$route.query.tab

    if (tabParam) {
      const tabIndex = this.activeTabs.findIndex((tab) => tab.id === tabParam)
      if (tabIndex !== -1) {
        this.activeTab = tabIndex
      }
    }

    // Check if navigating to specific network via query params
    const { billingCode: queryBillingCode, teamId: queryTeamId } = this.$route.query

    if (queryBillingCode) {
      logger.console.info(['=== Navigated with query params, fetching network data ===', { billingCode: queryBillingCode, teamId: queryTeamId }])

      // Guard: prevent the selectedSearch watcher from re-fetching while created() is handling this
      this.isCreatedFetching = true

      // Look up publisherId and networkCode from billingCode via API
      let publisherId = null
      let networkCode = null
      let publisherName = queryBillingCode
      try {
        const publisherResponse = await api.getPublishersByBillingCodes(queryBillingCode)
        const publisher = publisherResponse?.content?.[0]
        if (publisher) {
          publisherId = publisher.publisherId
          networkCode = publisher.networkCode
          publisherName = publisher.publisherName || publisher.clientName || queryBillingCode
          logger.console.info(['[Pacing] created() - Found publisherId from API:', publisherId, 'networkCode:', networkCode])
        }
      } catch (err) {
        logger.console.error(['[Pacing] created() - Error looking up publisherId:', err])
      }

      // Fallback to billingCode if API lookup failed (will likely return empty results)
      if (!publisherId) {
        logger.console.warn(['[Pacing] created() - No publisherId found, falling back to billingCode:', queryBillingCode])
        publisherId = queryBillingCode
      }

      // Set selectedSearch so SearchBar displays the correct selection
      const selectedSearch = {
        isSearchOption: true,
        route: 'reports',
        reportKey: 'pacing',
        reportId: queryBillingCode,
        billingCode: queryBillingCode,
        subId: queryTeamId || '-1',
        teamId: queryTeamId || '-1',
        reportName: 'pacing',
        reportPath: 'pacing',
        publisherId,
        networkCode,
        publisherName,
        text: publisherName,
      }
      this.$store.commit('tools/selectedSearch', selectedSearch)

      // Fetch data for this specific network and store in component
      const data = await this.fetchNetworkPacingData(publisherId, queryTeamId || '-1', networkCode)
      this.networkPacingData = data
      this.isCreatedFetching = false
      logger.console.info([`Fetched ${data.length} line items for network ${queryBillingCode} (publisherId: ${publisherId}, networkCode: ${networkCode})`])
      return
    }

    // Check if navigating to specific network via path params (e.g., /reports/pacing/BASS2011/-1)
    const { reportId: pathBillingCode, subId: pathTeamId } = this.$route.params

    if (pathBillingCode) {
      logger.console.info(['=== Navigated with path params, fetching network data ===', { billingCode: pathBillingCode, teamId: pathTeamId }])

      // Guard: prevent the selectedSearch watcher from re-fetching while created() is handling this
      this.isCreatedFetching = true

      // Look up publisherId and networkCode from billingCode via API
      let publisherId = null
      let networkCode = null
      let publisherName = pathBillingCode
      try {
        const publisherResponse = await api.getPublishersByBillingCodes(pathBillingCode)
        const publisher = publisherResponse?.content?.[0]
        if (publisher) {
          publisherId = publisher.publisherId
          networkCode = publisher.networkCode
          publisherName = publisher.publisherName || publisher.clientName || pathBillingCode
          logger.console.info(['[Pacing] created() - Found publisherId from API (path):', publisherId, 'networkCode:', networkCode])
        }
      } catch (err) {
        logger.console.error(['[Pacing] created() - Error looking up publisherId (path):', err])
      }

      // Fallback to billingCode if API lookup failed
      if (!publisherId) {
        logger.console.warn(['[Pacing] created() - No publisherId found (path), falling back to billingCode:', pathBillingCode])
        publisherId = pathBillingCode
      }

      // Set selectedSearch so SearchBar displays the correct selection
      const selectedSearch = {
        isSearchOption: true,
        route: 'reports',
        reportKey: 'pacing',
        reportId: pathBillingCode,
        billingCode: pathBillingCode,
        subId: pathTeamId || '-1',
        teamId: pathTeamId || '-1',
        reportName: 'pacing',
        reportPath: 'pacing',
        publisherId,
        networkCode,
        publisherName,
        text: publisherName,
      }
      this.$store.commit('tools/selectedSearch', selectedSearch)

      // Fetch data for this specific network and store in reportData (component-level)
      const data = await this.fetchNetworkPacingData(publisherId, pathTeamId || '-1', networkCode)
      this.reportData = data
      this.isCreatedFetching = false
      logger.console.info([`Fetched ${data.length} line items for network ${pathBillingCode} (publisherId: ${publisherId}, networkCode: ${networkCode}, path params)`])
    }

    // Dashboard insights are fetched by the showDashboard watcher (immediate: true)
    // No need to call here - that would cause duplicate API calls
  },
  methods: {
    /**
     * Fetch report data for the selected network (component-level data management)
     * Called when navigating to a specific network report view
     */
    async fetchReportData() {
      const { selectedSearch } = this

      // publisherId might not be set if client data wasn't enriched yet
      // Fallback to billingCode or reportId which are always available from cards
      const networkId = selectedSearch?.publisherId || selectedSearch?.billingCode || selectedSearch?.reportId
      const networkCode = selectedSearch?.networkCode

      if (!selectedSearch || !networkId) {
        logger.console.warn(['fetchReportData called without valid selectedSearch:', selectedSearch])
        return
      }

      const teamId = selectedSearch.teamId || selectedSearch.subId

      this.loadingReportData = true
      this.reportData = []

      try {
        logger.console.info([`[Pacing] fetchReportData - Fetching data for network ${networkId}, networkCode ${networkCode}, team ${teamId || 'all'}`])

        const data = await this.fetchNetworkPacingData(networkId, teamId || '-1', networkCode)

        this.reportData = data

        logger.console.info([`[Pacing] fetchReportData - Fetched ${data.length} line items`])
      } catch (error) {
        logger.console.error(['[Pacing] fetchReportData - Error:', error])

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Error loading pacing data: ${error.message}`,
          status: 'error',
        }, { root: true })
      } finally {
        this.loadingReportData = false
      }
    },

    /**
     * Fetch dashboard insights based on user type
     * External users: fetch all networks (limited scope)
     * Internal users: fetch assigned clients + all networks with pacing
     */
    async fetchDashboardInsights() {
      logger.console.info(['=== fetchDashboardInsights called ==='])
      logger.console.info([`shouldFetchAllNetworks: ${this.shouldFetchAllNetworks}`])
      logger.console.info([`isUserExternal: ${this.isUserExternal}`])
      logger.console.info([`isInternalUser: ${this.isInternalUser}`])

      if (this.shouldFetchAllNetworks) {
        logger.console.info(['Taking external user path: fetchAllNetworkInsights'])
        await this.fetchAllNetworkInsights()
        return
      }

      // For internal users: fetch both assigned clients and all networks
      // Run both fetches in parallel for better performance
      logger.console.info(['Taking internal user path: Promise.all with both fetches'])

      await Promise.all([
        this.fetchAssignedClientInsights(),
        this.fetchAllNetworksWithPacing().then((networks) => {
          logger.console.info([`fetchAllNetworksWithPacing returned ${networks.length} networks`])
          this.allNetworksData = networks
        }),
      ])
    },

    // Helper to create publisher with its teams
    makePublisherWithTeams(pub) {
      return (response) => {
        const teams = response?.content || []

        logger.console.info([`Publisher ${pub.publisherId} (${pub.billingCode}) has ${teams.length} teams:`, teams])

        return {
          teams,
          publisher: pub,
        }
      }
    },

    // Helper to handle errors when fetching teams for a publisher
    catchPublisherTeamsError(pub) {
      return (error) => {
        logger.console.warn([`Failed to load teams for publisher ${pub.publisherId}:`, error])

        return {
          teams: [],
          publisher: pub,
        }
      }
    },

    /**
     * @function filterTeamsWithoutAllTeams
     * @desc Filter out teams with teamId -1 and teamCode -1
     * @param {Array<Object>} teams - Array of team objects.
     * @returns {Array<Object>} - Filtered array of teams
     *
     * @note OpenAPI Specification
     * {
     *   teamCode: bigint,
     *   teamId: integer,
     *   teamName: string
     * }
     */
    filterTeamsWithoutAllTeams(teams) {
      return teams.filter((team) => team.teamId !== -1 && team.teamCode !== -1)
    },

    /**
     * @function fetchAllNetworksWithPacing
     * @desc Fetch all publishers, their teams, and their clients using
     * a progressive loading strategy.
     *
     * @note Publisher-Teams Request Optimization
     * This function fetches all publishers, as they all have pacing enabled.
     * For each publisher, fetch its teams. Then, fetch all clients and match
     * them to publishers via billingCode.
     *
     * Progressive Loading Strategy:
     * 1. Load publishers first (fast) - show cards with "All Teams" immediately
     * 2. Load teams in background - expand cards by teams
     * 3. Load clients last - match to publishers and enrich with client names
     *
     * Each publisher has multiple teams. We create one card per
     * publisher-team combination. This matches how SearchBar works
     * when building search options for pacing.
     */
    async fetchAllNetworksWithPacing() {
      try {
        logger.console.info(['Fetching all networks with progressive loading'])

        // Set loading state.
        this.loadingPublishers = true

        // PHASE 1: Load publishers first (fast, immediate display with "All Teams" placeholder)
        const publishersResponse = await api.getPublishers()

        const publishers = publishersResponse?.content || []

        // Log the number of publishers loaded in phase 1
        logger.console.info([`Phase 1: Loaded ${publishers.length} publishers - creating placeholder cards`])

        // Initially create one "All Teams" card per publisher for immediate display
        const publisherPlaceholders = publishers.map((pub) => ({
          publisherId: pub.publisherId,
          networkCode: pub.publisherId,
          publisherName: pub.name,
          billingCode: pub.billingCode,
          teamId: -1,
          teamCode: -1,
          teamName: 'All Teams',
          clientName: null,
          loadingTeams: true,
          loadingClientData: true,
        }))

        // Set initial data with placeholders.
        this.allNetworksData = publisherPlaceholders

        // Update loading state.
        this.loadingPublishers = false

        // PHASE 2: Fetch teams for each publisher and expand cards
        logger.console.info(['Phase 2: Fetching teams for each publisher in background'])

        /**
         * @const teamsPromises
         * @type {Promise<Array<{publisher: Object, teams: Array<Object>}>>}
         * @desc Fetch teams for all publishers in parallel
         * Each promise resolves to an object with publisher and its teams,
         * or empty teams array on error.
         *
         */
        // eslint-disable-next-line arrow-body-style
        const teamsPromises = publishers.map((pub) => {
          return api.getTeams(pub.publisherId)
            .then(this.makePublisherWithTeams(pub))
            .catch(this.catchPublisherTeamsError(pub))
        })

        // Wait for all teams to be fetched.
        const publishersWithTeams = await Promise.all(teamsPromises)

        // Expand publishers into publisher-team combinations.
        const publisherTeamCards = []

        publishersWithTeams.forEach(({ publisher: pub, teams }) => {
          // Check if publisher has team functionality enabled
          const hasTeamsEnabled = pub.hasTeam === true

          if (hasTeamsEnabled) {
            if (teams.length > 0) {
              // Create a card for each team.
              teams.forEach((team) => {
                publisherTeamCards.push({
                  // Client is unknown at this stage.
                  clientId: null,
                  clientName: null,

                  publisherId: pub.publisherId,
                  publisherName: pub.name,

                  billingCode: pub.billingCode,
                  networkCode: pub.networkCode,

                  teamCode: team.teamCode,
                  teamId: team.teamId,
                  teamName: `${team.teamName || `Team ${team.teamId}`}`,

                  loadingClientData: true,
                  loadingTeams: false,
                })
              })
            } else {
              // Has team functionality but no real teams - create "All Teams" card
              publisherTeamCards.push({
                // Client is unknown at this stage.
                clientId: null,
                clientName: null,

                publisherId: pub.publisherId,
                publisherName: pub.name,

                billingCode: pub.billingCode,
                networkCode: pub.networkCode,

                teamCode: -1,
                teamId: -1,
                teamName: 'All Teams',

                loadingClientData: true,
                loadingTeams: false,
              })
            }
          } else {
            // No team functionality - create card with publisher name + ": All Teams"
            publisherTeamCards.push({
              // Client is unknown at this stage.
              clientId: null,
              clientName: null,

              publisherId: pub.publisherId,
              publisherName: pub.name,

              billingCode: pub.billingCode,
              networkCode: pub.networkCode,

              teamCode: -1,
              teamId: -1,
              teamName: 'All Teams',

              loadingClientData: true,
              loadingTeams: false,
            })
          }
        })

        // log the last one for verification
        logger.console.info(['Last publisher-team card created:', publisherTeamCards[publisherTeamCards.length - 1]])

        // Update data with expanded publisher-team cards
        this.allNetworksData = publisherTeamCards

        // @remove after testing
        logger.console.info([`Phase 2 complete: Expanded ${publishers.length} publishers into ${publisherTeamCards.length} publisher-team cards`])

        // PHASE 3: Load clients and match to publishers

        // Set loading state for client matching
        this.loadingClientMatching = true

        // Fetch all clients.
        const clientsResponse = await api.getClients()
        const clients = clientsResponse?.content || []

        // @remove after testing
        logger.console.info([`Phase 3: Loaded ${clients.length} clients, matching to publishers`])

        // Create client lookup by billingCode
        const clientsByBillingCode = {}

        clients.forEach((client) => {
          if (client.billingCode) {
            clientsByBillingCode[client.billingCode] = client
          }
        })

        // Update network data with client information
        this.allNetworksData = this.allNetworksData.map((network) => {
          const client = clientsByBillingCode[network.billingCode]

          if (client) {
            return {
              ...network,
              clientId: client.clientId,
              clientName: client.clientName,
              loadingClientData: false,
            }
          }

          // No matching client - keep publisher-only data
          return {
            ...network,
            // clientId: null, // No matching client so clientId remains null.
            // clientName: null, // No matching client so remains null.
            loadingClientData: false,
          }
        })

        // Finalize loading state.
        this.loadingClientMatching = false

        // Log summary of matched networks
        const networksWithClients = this.allNetworksData.filter((n) => n.clientName)

        // @remove after testing
        logger.console.info([`Progressive loading complete: ${networksWithClients.length}/${this.allNetworksData.length} networks have matching clients`])

        // Return the publisher-teams.
        return this.allNetworksData
      } catch (err) {
        logger.console.error(['Error in fetchAllNetworksWithPacing:', err])

        this.loadingPublishers = false
        this.loadingClientMatching = false

        return []
      }
    },

    /**
     * Fetch insights for internal users based on their assigned clients
     */
    async fetchAssignedClientInsights() {
      this.loadingDashboardInsights = true

      this.$log.info('=== fetchAssignedClientInsights started ===')
      this.$log.info(`Using personId: ${this.currentPersonId}`)

      try {
        // 1. Get assigned clients for this person
        const response = await api.getClientAssignments(this.currentPersonId)
        const { content, error } = response || {}

        this.$log.info('Client assignments response:', response)

        if (error) {
          logger.console.error(['Error fetching client assignments:', error])
          this.assignedClients = []
          return
        }

        this.assignedClients = content || []

        logger.console.info([
          `Fetched ${this.assignedClients.length} assigned clients for personId: ${this.currentPersonId}`,
        ])

        if (this.assignedClients.length === 0) {
          this.$log.warn('No assigned clients found after assignment!')
          return
        }

        // 2. Extract billing codes for fetching insights
        const billingCodes = this.assignedClients.map((a) => a.billingCode).filter(Boolean)

        logger.console.info(['Assigned client billing codes:', billingCodes])

        // 3. Get publishers list to map billingCode to publisherId and networkCode
        const publishersResponse = await api.getPublishers()
        const publishers = publishersResponse?.content || []

        // Create lookup map: billingCode -> publisher
        const publishersByBillingCode = {}
        publishers.forEach((pub) => {
          if (pub.billingCode) {
            publishersByBillingCode[pub.billingCode] = pub
          }
        })

        this.$log.info(`Building networks for ${this.assignedClients.length} assigned clients`)

        // 4. Build network list by matching assigned clients with publishers
        const assignedNetworks = this.assignedClients
          .map((client) => {
            const publisher = publishersByBillingCode[client.billingCode]
            if (!publisher) {
              logger.console.warn([`No publisher found for billingCode: ${client.billingCode}`])
              return null
            }

            return {
              text: client.clientName,
              billingCode: client.billingCode,
              publisherId: publisher.publisherId,
              networkCode: publisher.networkCode,
              publisherName: publisher.name,
              teamId: -1, // Fetch all teams for this network
            }
          })
          .filter(Boolean) // Remove null entries

        this.$log.info('Assigned networks to fetch:', assignedNetworks.map((n) => ({
          text: n.text,
          billingCode: n.billingCode,
          publisherId: n.publisherId,
          networkCode: n.networkCode,
        })))

        // 5. Fetch pacing data for assigned networks
        const validNetworks = assignedNetworks

        if (validNetworks.length === 0) {
          logger.console.warn(['No valid networks found after matching with publishers'])

          this.dashboardInsights = {
            assignedClients: this.assignedClients,
            billingCodes,
            pacingData: [],
          }
          return
        }

        const pacingDataPromises = validNetworks.map((network) => this.fetchNetworkPacingData(network.publisherId, network.teamId, network.networkCode))

        const pacingDataArrays = await Promise.all(pacingDataPromises)

        // Flatten all pacing data into single array
        const allPacingData = pacingDataArrays.flat()

        this.dashboardInsights = {
          assignedClients: this.assignedClients,
          enrichedClients: assignedNetworks, // Store enriched data with publisherId/networkCode
          billingCodes,
          pacingData: allPacingData,
        }

        logger.console.info([`Fetched pacing data for ${assignedNetworks.length} assigned networks, ${allPacingData.length} total line items`])
      } catch (err) {
        logger.console.error(['Error in fetchAssignedClientInsights:', err])
      } finally {
        this.loadingDashboardInsights = false
      }
    },

    /**
     * Fetch insights for external users across all their networks
     * Uses progressive loading: show publisher cards immediately, load client data in background
     */
    async fetchAllNetworkInsights() {
      this.loadingDashboardInsights = true

      try {
        logger.console.info(['Fetching all network insights for external user with progressive loading'])

        // External users should only see their assigned networks, not all publishers.
        // Get billingCodes from the user's person doc network[] array.
        const userNetwork = this.currentUser?.network || []
        const userBillingCodes = userNetwork
          .map((n) => n?.organization?.code)
          .filter(Boolean)

        if (userBillingCodes.length === 0) {
          logger.console.warn(['External user has no billingCodes in network assignment'])
          this.dashboardInsights = { scope: 'all-networks', pacingData: [] }
          return
        }

        logger.console.info([`External user billingCodes: [${userBillingCodes.join(', ')}]`])

        // Fetch only the user's assigned publishers (not all active publishers)
        this.loadingPublishers = true
        const publishersResponse = await api.getPublishersByBillingCodes(userBillingCodes)
        const publishers = publishersResponse?.content || []

        logger.console.info([`Loaded ${publishers.length} publishers for external user`])

        if (publishers.length === 0) {
          this.loadingPublishers = false
          this.dashboardInsights = { scope: 'all-networks', pacingData: [] }
          return
        }

        // Build the user's team assignments lookup: { billingCode: [teamCode, ...] }
        const userTeamsByOrg = {}
        userNetwork.forEach((n) => {
          const orgCode = n?.organization?.code
          const teams = Array.isArray(n?.team) ? n.team : []

          if (orgCode && teams.length > 0) {
            userTeamsByOrg[orgCode] = teams
              .map((t) => ({ code: t?.code, id: t?.id }))
              .filter((t) => t.code || t.id)
          }
        })

        // Create publisher-team cards scoped to user's assignments
        const makeAllTeamsCard = (pub) => ({
          publisherId: pub.publisherId,
          publisherName: pub.name,
          billingCode: pub.billingCode,
          networkCode: pub.networkCode,
          teamId: -1,
          teamCode: -1,
          teamName: 'All Teams',
          clientName: pub.clientName || null,
          loadingTeams: false,
          loadingClientData: false,
        })

        const cardArrays = await Promise.all(
          publishers.map(async (pub) => {
            const assignedTeams = userTeamsByOrg[pub.billingCode] || []
            const assignedTeamCodes = assignedTeams.map((t) => String(t.code || t.id))

            if (assignedTeams.length === 0) {
              return [makeAllTeamsCard(pub)]
            }

            // Fetch teams from API, scoped to user's assigned teams
            try {
              const assignedTeamIds = assignedTeams.map((t) => t.id).filter(Boolean)
              const teamsResponse = await api.getTeams(pub.publisherId, assignedTeamIds)
              const allTeams = teamsResponse?.content || []
              const matchingTeams = allTeams.filter(
                (team) => assignedTeamCodes.includes(String(team.teamCode))
                  || assignedTeamCodes.includes(String(team.teamId)),
              )

              if (matchingTeams.length === 0) {
                return [makeAllTeamsCard(pub)]
              }

              return matchingTeams.map((team) => ({
                publisherId: pub.publisherId,
                publisherName: pub.name,
                billingCode: pub.billingCode,
                networkCode: pub.networkCode,
                teamCode: team.teamCode,
                teamId: team.teamId,
                teamName: team.teamName || `Team ${team.teamId}`,
                clientName: pub.clientName || null,
                loadingTeams: false,
                loadingClientData: false,
              }))
            } catch (err) {
              logger.console.error([`Error fetching teams for ${pub.billingCode}:`, err])

              // Fall back to user's assigned team info from person doc
              // so pacing calls still filter by teamId
              return assignedTeams.map((t) => ({
                publisherId: pub.publisherId,
                publisherName: pub.name,
                billingCode: pub.billingCode,
                networkCode: pub.networkCode,
                teamCode: t.code ? Number(t.code) : -1,
                teamId: t.id ? Number(t.id) : -1,
                teamName: `Team ${t.id || t.code}`,
                clientName: pub.clientName || null,
                loadingTeams: false,
                loadingClientData: false,
              }))
            }
          }),
        )

        this.allNetworksData = cardArrays.flat()
        this.loadingPublishers = false

        // Get all networks from allNetworksData (now populated by progressive loading)
        const networks = this.allNetworksData
          .map((network) => ({
            publisherId: network.publisherId,
            networkCode: network.networkCode,
            teamId: network.teamId || -1,
          }))
          .filter((network) => network.publisherId) // Filter out networks without valid publisherId

        if (networks.length === 0) {
          logger.console.warn(['No valid networks with publisherId found in allNetworksData'])
          this.dashboardInsights = {
            scope: 'all-networks',
            pacingData: [],
          }
          return
        }

        // Fetch pacing data for each network in background
        const pacingDataPromises = networks.map((network) => this.fetchNetworkPacingData(network.publisherId, network.teamId, network.networkCode))

        const pacingDataArrays = await Promise.all(pacingDataPromises)

        // Flatten all pacing data into single array
        const allPacingData = pacingDataArrays.flat()

        // Set dashboard insights.
        this.dashboardInsights = {
          scope: 'all-networks',
          pacingData: allPacingData,
        }

        logger.console.info([`Fetched pacing data for ${networks.length} networks, ${allPacingData.length} total line items`])
      } catch (err) {
        logger.console.error(['Error in fetchAllNetworkInsights:', err])
      } finally {
        this.loadingDashboardInsights = false
      }
    },

    /**
     * Refresh all data (called by Refresh button)
     */
    async refreshData() {
      this.isRefreshing = true
      try {
        // Check if viewing a specific network via query params
        const { billingCode, teamId } = this.$route.query

        if (billingCode) {
          // Refresh network-specific data (query param navigation)
          // Get networkCode from selectedSearch if available
          const networkCode = this.selectedSearch?.networkCode
          const data = await this.fetchNetworkPacingData(billingCode, teamId || '-1', networkCode)
          this.networkPacingData = data
        } else if (this.showDashboard) {
          // Refresh dashboard insights
          await this.fetchDashboardInsights()
        } else {
          // Refresh report data (selectedSearch navigation)
          await this.fetchReportData()
        }
      } finally {
        this.isRefreshing = false
      }
    },

    /**
     * Fetch pacing data for a specific network
     * @param {string} publisherId - Publisher ID for API call
     * @param {string} teamId - Team ID for filtering (optional, '-1' means all teams)
     * @returns {Promise<Array>} Array of pacing line items (includes publisherId and teamIds from API)
     */
    async fetchNetworkPacingData(publisherId, teamId, networkCode) {
      // Validate publisherId
      if (!publisherId) {
        logger.console.error(['fetchNetworkPacingData called with undefined publisherId'])
        return []
      }

      try {
        /**
         * @note API Call Explanation
         * We omit the limit parameter to fetch all line items for the network.
         * The API returns all line items associated with the given
         * publisherId and teamId (if provided). See the OpenAPI
         * specification for `/reports/pacing_line_items_summary`
         * for more details.
         *
         * @const {Object} apiQueryParams - Parameters for API call
         * @param {number} apiQueryParams.publisherId - Publisher ID (iAdOps internal ID)
         * @param {number} apiQueryParams.teamId - Team ID (-1 for all teams)
         *
         * @const {Object} response - API response object
         * @param {Array} response.content - Array of pacing line items from API
         * @param {string} response.kind - Resource kind identifier
         * @param {string} response.self - Link to the current API resource
         * @param {number} response.totalCount - Total count of items returned from API
         * @param {Boolean|Error|Object} response.error - False or an error object
         *
         * @todo Refactor API error handling toward a standardized approach
         * across all API calls in the application for consistency.
         */
        const apiQueryParams = {
          publisherId,
          teamId,
        }

        const response = await api.getPacingDataForNetwork(apiQueryParams)

        const { content, error } = response || {}

        if (error) {
          logger.console.error([`Error fetching pacing data for network ${publisherId}:`, error])
          return []
        }

        // Ensure content is an array before mapping
        if (!Array.isArray(content)) {
          logger.console.warn([`Pacing Publisher ${publisherId} returned non-array content:`, content])
          return []
        }

        // Apply field mappings matching transform.pacing() so columns render correctly.
        // The API returns fields like gamPriority, goal, code which need to be mapped
        // to priority, booked, lineItemCode, etc. for the column definitions.
        const mapItem = (item) => {
          const mappedItem = transform.assignCustomFields({ ...item })

          mappedItem.allRevenue = (mappedItem.allRevenue || 0) / 1000000
          mappedItem.booked = mappedItem.goal
          mappedItem.lineItemCode = mappedItem.code
          mappedItem.potentialLostRevenue = (mappedItem.potentialLostRevenue || 0) / 1000000
          mappedItem.priority = mappedItem.gamPriority
          mappedItem.rate = (mappedItem.rate || 0) / 1000000
          mappedItem.osi = transform.getOsiView(mappedItem.type, mappedItem.booked, mappedItem.osi)

          // Enrich with network identifiers so dashboard cards can filter
          // by publisherId and teamIds (ReportList.getNetworkData,
          // EnhancedNetworkCard.calculateTabCount)
          mappedItem.publisherId = publisherId
          if (!mappedItem.teamIds) {
            mappedItem.teamIds = teamId && teamId !== '-1' ? [teamId] : []
          }

          if (networkCode) {
            mappedItem.networkCode = networkCode
            mappedItem.lineItemGamLink = gam.getLineItemUrl(networkCode, mappedItem.code)
            mappedItem.orderGamLink = gam.getOrderUrl(networkCode, mappedItem.orderCode)
          }

          return mappedItem
        }

        return content.map(mapItem)
      } catch (err) {
        logger.console.error([`Exception fetching pacing data for publisher ${publisherId}:`, err])
        return []
      }
    },

    goToBurtForecast() {
      /**
       * @note Regarding the NoOpener,NoReferrer Policy
       * It's generally recommended to add rel="noopener noreferrer"
       * to links using target="_blank". When using window.open(),
       * we achieve a similar effect by passing noopener and
       * noreferrer as the third argument. This prevents the new
       * tab from accessing the original page's window.opener
       * object, mitigating potential security risks.
       */

      window.open(this.linkToBurtForecast, '_blank', 'noopener,noreferrer')
    },
    goToBurtHub() {
      /**
       * @note Regarding the NoOpener,NoReferrer Policy
       * It's generally recommended to add rel="noopener noreferrer"
       * to links using target="_blank". When using window.open(),
       * we achieve a similar effect by passing noopener and
       * noreferrer as the third argument. This prevents the new
       * tab from accessing the original page's window.opener
       * object, mitigating potential security risks.
       */

      window.open(this.linkToBurtHub, '_blank', 'noopener,noreferrer')
    },

    /**
     * Update URL with tab parameter for bookmarkable states
     * @param {String} tabId - Tab identifier (all, at-risk, etc.)
     */
    updateUrlWithTab(tabId) {
      logger.console.info(['🔄 [ROUTE-DEBUG] updateUrlWithTab called', {
        tabId,
        currentQuery: this.$route.query,
        stackTrace: new Error().stack,
      }])
      const currentQuery = this.$route.query || {}
      const cleanedQuery = this.cleanQueryParameters(currentQuery)

      // For default tabs, remove the tab parameter to keep URL clean
      if (tabId === 'assigned-networks' || (tabId === 'all-networks' && this.isUserExternal)) {
        const { tab, ...queryWithoutTab } = cleanedQuery
        const newQuery = queryWithoutTab

        // Only update route if query actually changed
        const currentQueryString = JSON.stringify(currentQuery)
        const newQueryString = JSON.stringify(newQuery)

        if (currentQueryString !== newQueryString) {
          logger.console.info(['🚀 [ROUTE-DEBUG] router.push (default tab - removing tab param)', {
            from: currentQuery,
            to: newQuery,
            tabId,
          }])
          this.$router.push({ query: newQuery }).catch(() => {})
        }

        return
      }

      // Add or update tab parameter for other tabs
      const newQuery = {
        ...cleanedQuery,
        tab: tabId,
      }

      // Only update route if query actually changed
      const currentQueryString = JSON.stringify(currentQuery)
      const newQueryString = JSON.stringify(newQuery)

      if (currentQueryString !== newQueryString) {
        logger.console.info(['🚀 [ROUTE-DEBUG] router.push (adding tab param)', {
          from: currentQuery,
          to: newQuery,
          tabId,
        }])
        this.$router.push({ query: newQuery }).catch(() => {})
      }
    },

    /**
     * Clean URL query parameters by keeping only recognized parameters
     * @param {Object} currentQuery - Current route query object
     * @returns {Object} Cleaned query object with only valid parameters
     */
    cleanQueryParameters(currentQuery) {
      // Define the whitelist of recognized query parameters for the Pacing component
      const validParameters = ['tab', 'publisher', 'team', 'startDate', 'endDate', 'lineItem', 'order', 'status', 'billingCode', 'teamId', 'teamCode', 'networkCode', 'report']

      const cleanedQuery = {}

      // Only keep parameters that are in our whitelist
      validParameters.forEach((param) => {
        if (currentQuery[param] !== undefined) {
          cleanedQuery[param] = currentQuery[param]
        }
      })

      return cleanedQuery
    },

    // Local summary calculation methods
    /**
     * Convert summary parameters to filter format and count matching items
     * This uses the same FILTER.report logic that filters the main data table
     * @param {Array} data - Items to filter
     * @param {Object} summaryConfig - Summary config from summaries.js with parameters
     * @returns {Number} Count of items matching filter
     */
    calculateSummaryCount(data, summaryConfig) {
      if (!summaryConfig || !summaryConfig.parameters) {
        return data.length
      }

      logger.console.info(['calculateSummaryCount called', {
        summaryId: summaryConfig.id,
        dataLength: data.length,
        parameters: summaryConfig.parameters,
      }])

      // Convert summary parameters to filter format
      const filterData = {}
      const paramKeys = Object.keys(summaryConfig.parameters)

      paramKeys.forEach((key) => {
        const paramFunction = summaryConfig.parameters[key]
        if (typeof paramFunction === 'function') {
          const filterResult = paramFunction()

          logger.console.info([`Parameter "${key}" result:`, filterResult])

          // Handle different return formats from summary parameters:
          // 1. Array of { operator, value } objects (e.g., status, osi)
          // 2. Single { operator, value } object (e.g., startDate)
          if (Array.isArray(filterResult)) {
            // Keep the full array of { operator, value } objects
            // FILTER.report expects this format for normalizeFilterValues
            filterData[key] = filterResult
          } else if (filterResult && typeof filterResult === 'object') {
            // For single objects (usually date filters), wrap in array
            // to match the expected format
            filterData[key] = filterResult
          }
        }
      })

      logger.console.info(['Filter data created:', filterData])

      const filterKeys = Object.keys(filterData)

      // If no filters, return all data
      if (filterKeys.length === 0) {
        return data.length
      }

      // Use FILTER.report to check each item (same logic as table filtering)
      const reportFilter = filter.report.bind(filter)
      const isItemIncluded = (item) => reportFilter(item, filterData, filterKeys)

      const matchingItems = data.filter(isItemIncluded)

      logger.console.info([`Matching items count: ${matchingItems.length}`])

      // Log first item structure for debugging
      if (data.length > 0) {
        logger.console.info(['First data item structure:', Object.keys(data[0])])
      }

      return matchingItems.length
    },

    getDeliverySubtitle() {
      // Create contextual subtitle based on current filters/date range
      return this.currentItems === 1 ? 'line item' : 'line items'
    },

    numberFormatter(value) {
      return format.number(value)
    },

    /**
     * Show the viewability column in the data table
     * Updates the column visibility to make viewabilityPercentage visible
     */
    showViewabilityColumn() {
      const reportKey = this.$store.getters['tools/reportKey']
      const currentColumns = this.$store.state.tools.columns[reportKey]

      if (!currentColumns || !reportKey) {
        logger.console.info(['Cannot show viewability column - missing columns or reportKey'])
        return
      }

      // Find and update the viewabilityPercentage column
      const updatedColumns = currentColumns.map((column) => {
        if (column.key === 'viewabilityPercentage') {
          return {
            ...column,
            visible: true,
          }
        }
        return column
      })

      // Commit the updated columns to store
      this.$store.commit('tools/columns', { [reportKey]: updatedColumns })

      logger.console.info(['Viewability column visibility set to true'])
    },

    /**
     * Apply tab and search filters to items
     * Used for filtering items within groups when grouping is active
     */
    applyFiltersToItems(items) {
      // First apply tab filter
      // Get filter data for current tab
      const filterData = {}
      const filterKeys = []

      Object.keys(this.currentTab.summaryParameters || {}).forEach((key) => {
        const filterResult = this.currentTab.summaryParameters[key]
        if (filterResult) {
          if (Array.isArray(filterResult)) {
            filterData[key] = filterResult
          } else if (typeof filterResult === 'object') {
            filterData[key] = filterResult
          }
          filterKeys.push(key)
        }
      })

      // Apply tab filter
      let tabFilteredItems = items
      if (filterKeys.length > 0) {
        const reportFilter = filter.report.bind(filter)
        const isItemIncluded = (item) => reportFilter(item, filterData, filterKeys)
        tabFilteredItems = items.filter(isItemIncluded)
      }

      // Apply search filter
      if (!this.searchText || this.searchText.trim() === '') {
        return tabFilteredItems
      }

      const searchLower = this.searchText.toLowerCase().trim()
      return tabFilteredItems.filter((item) => {
        const lineItemName = (item.lineItemName || '').toLowerCase()
        const orderName = (item.orderName || '').toLowerCase()
        const advertiserName = (item.advertiserName || '').toLowerCase()
        const status = (item.status || '').toLowerCase()

        return lineItemName.includes(searchLower)
          || orderName.includes(searchLower)
          || advertiserName.includes(searchLower)
          || status.includes(searchLower)
      })
    },

    exportAsCsv() {
      const {
        currentColumns: exportColumns,
        currentGroups: exportGroups,
        reportName: exportName,
      } = this
      const exportFormat = 'csv'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },

    exportAsXlsx() {
      const {
        currentColumns: exportColumns,
        currentGroups: exportGroups,
        reportName: exportName,
      } = this
      const exportFormat = 'xlsx'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },

    /**
     * Handle dashboard sorting changes from self-managed ToolsToolbar
     * @param {Array} sorts - Array of { column, direction } objects
     */
    onDashboardSortingChange(sorts) {
      this.dashboardSortConfig = sorts || []
    },

    /**
     * Handle dashboard grouping changes from self-managed ToolsToolbar
     * @param {Array} groups - Array of groupBy property values
     */
    onDashboardGroupingChange(groups) {
      this.dashboardGroupByProperties = groups || []
    },

    /**
     * Handle dashboard column visibility changes from self-managed ToolsToolbar
     * @param {Array} updatedColumns - Updated array of column objects with visible property
     */
    onDashboardColumnsChange(updatedColumns) {
      this.dashboardColumns = updatedColumns.map((col) => ({
        text: col.text || col.columnName,
        value: col.value,
        visible: col.visible !== false,
      }))
    },

    /**
     * Scroll to a specific group in the table
     * @param {String} groupName - The group name to scroll to
     */
    scrollToGroup(groupName) {
      // First, ensure the group is expanded
      if (this.showRows.indexOf(groupName) === -1) {
        this.$store.dispatch('tools/toggleRows', groupName)
      }

      // Wait for DOM update, then scroll
      this.$nextTick(() => {
        // Find the group element by looking for the v-row with matching group name
        const groupElements = document.querySelectorAll('.table-group')
        const targetGroup = Array.from(groupElements).find((el) => {
          const labelText = el.textContent
          return labelText && labelText.includes(groupName)
        })

        if (targetGroup) {
          const yOffset = -80 // Offset for sticky headers
          const y = targetGroup.getBoundingClientRect().top + window.pageYOffset + yOffset

          window.scrollTo({ top: y, behavior: 'smooth' })
        }
      })
    },

    /**
     * Handle clear all filters (called from external sources)
     */
    handleClearFilters() {
      this.localFilterData = {}
      this.localFilterKeys = []

      logger.console.info(['[Pacing] Filters cleared'])
    },

    /**
     * Handle grouping changes from ToolsDrawer
     * @param {Array} groupProperties - The new grouping properties
     */
    onGroupingChange(groupProperties) {
      logger.console.info(['[Pacing] Grouping changed to:', groupProperties])
      // Grouping is already applied via store dispatch in ToolsDrawer
      // Copy to local state if needed for component-level grouping
      this.localGroupByProperties = [...(groupProperties || [])]
    },

    /**
     * Handle sorting changes from ToolsDrawer
     * @param {Array} sortConfig - The new sorting configuration
     */
    onSortingChange(sortConfig) {
      logger.console.info(['[Pacing] Sorting changed to:', sortConfig])
      // Sorting is already applied via store dispatch in ToolsDrawer
    },

    /**
     * Handle successful client assignment
     * Refresh dashboard insights to show newly assigned clients
     */
    async handleClientsAssigned() {
      this.$log.info('handleClientsAssigned called')
      this.$log.info(`Current personId: ${this.currentPersonId}`)

      // Show initial success message
      this.$store.commit('tools/snackbar', true)
      this.$store.commit('tools/snack', {
        message: 'Clients assigned successfully. Loading pacing data...',
        status: 'success',
      })

      // Wait a moment for the database transaction to commit
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Refresh dashboard insights to load data for newly assigned clients
      await this.fetchDashboardInsights()

      this.$log.info('Dashboard insights refreshed after assignment')
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

// ============================================================================
// COMPACT HEADER STYLES
// ============================================================================

.pacing-view-header-compact {
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

  .header-toolbar {
    border-bottom: none;
  }

  .header-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: rgba(var(--v-primary-base), 0.08);
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--v-primary-base), 0.12);
    }
  }

  // Inline tabs styling
  .pacing-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      height: 56px;
      background-color: transparent !important;
    }

    ::v-deep .v-tab {
      height: 56px;
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.25px;
      text-transform: none;
      padding: 0 16px;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(0, 0, 0, 0.04);
      }
    }

    ::v-deep .v-tabs-slider {
      height: 3px;
      border-radius: 3px 3px 0 0;
    }
  }

  // Tab count chips styling
  .tab-count-chip {
    background-color: #e0e0e0 !important;
    border-color: #bdbdbd !important;
    color: #616161 !important;
    font-weight: 600;
  }

  .header-actions {
    gap: 8px;
  }

  // Controls bar
  .controls-bar {
    border-radius: 0; // No border-radius since it's inside .tabs-section
    margin: 0; // No margin since it's inside .tabs-section

    .search-field-compact {
      ::v-deep .v-input__control {
        min-height: 38px !important;
      }

      ::v-deep .v-input__slot {
        min-height: 38px !important;
        padding: 0 12px !important;
        border-radius: 6px;
        background-color: white;
      }

      ::v-deep fieldset {
        border-color: rgba(0, 0, 0, 0.23);
      }

      .search-icon {
        color: rgba(0, 0, 0, 0.54);
        margin-right: 8px;
      }
    }

    // Button styling to match Time.vue
    .v-btn {
      text-transform: none;
      letter-spacing: 0.25px;
      font-weight: 500;
      height: 36px !important;
      border-radius: 6px;
    }
  }
}

// Dark theme support
.theme--dark .pacing-view-header-compact {
  background-color: #2d2d2d;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);

  .header-toolbar {
    background-color: #2d2d2d !important;
  }

  .header-icon-wrapper {
    background: rgba(var(--v-primary-base), 0.15);

    &:hover {
      background: rgba(var(--v-primary-base), 0.2);
    }
  }

  .pacing-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      background-color: #2d2d2d !important;
    }

    ::v-deep .v-tab:hover {
      background-color: rgba(255, 255, 255, 0.05);
    }

    .tab-count-chip {
      background-color: #424242 !important;
      border-color: #616161 !important;
      color: #e0e0e0 !important;
    }
  }

  .controls-bar {
    background-color: rgba(255, 255, 255, 0.05);

    .search-field-compact {
      ::v-deep .v-input__slot {
        background-color: rgba(255, 255, 255, 0.05);
      }

      ::v-deep fieldset {
        border-color: rgba(255, 255, 255, 0.23);
      }

      .search-icon {
        color: rgba(255, 255, 255, 0.54);
      }
    }
  }
}

// Interactive states for search field
.pacing-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input--is-focused fieldset {
  border-color: var(--v-primary-base);
  border-width: 2px;
}

.pacing-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input__slot:hover fieldset {
  border-color: rgba(var(--v-primary-base), 0.5);
}

.theme--dark .pacing-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input--is-focused fieldset {
  border-color: var(--v-primary-base);
  border-width: 2px;
}

.theme--dark .pacing-view-header-compact .controls-bar .search-field-compact ::v-deep .v-input__slot:hover fieldset {
  border-color: rgba(var(--v-primary-base), 0.5);
}

// Responsive adjustments
@media (max-width: 960px) {
  .pacing-view-header-compact {
    .header-toolbar {
      height: auto !important;
      min-height: 48px !important;
    }

    .header-icon-wrapper {
      width: 36px;
      height: 36px;
    }

    .pacing-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        height: 48px !important;
      }

      ::v-deep .v-tab {
        font-size: 0.813rem;
        min-width: 80px;
        padding: 0 12px;
        height: 48px;
      }
    }
  }
}

@media (max-width: 768px) {
  .pacing-view-header-compact {
    .header-icon-wrapper {
      width: 32px;
      height: 32px;
      margin-right: 8px !important;
    }

    .pacing-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        height: 44px !important;
      }

      ::v-deep .v-tab {
        font-size: 0.75rem;
        min-width: 70px;
        padding: 0 8px;
        height: 44px;
      }

      ::v-deep .v-tabs-slider {
        height: 2px !important;
      }
    }

    .controls-bar {
      .search-field-compact {
        ::v-deep .v-input__control {
          min-height: 36px !important;
        }

        ::v-deep .v-input__slot {
          min-height: 36px !important;
        }
      }

      .v-btn {
        height: 30px !important;
        font-size: 0.813rem;
      }
    }
  }
}

// ============================================================================
// EXISTING STYLES
// ============================================================================

::v-deep.filter-link {
  margin-top: 8px;
}

::v-deep.filter-content {
  margin-top: 16px;
}

::v-deep.filter-label {
  padding-top: 20px;
}

.delivery-table-filter {
  .first-row {
    display: flex;

    .v-text-field {
      margin: 0 24px;
      padding-top: 0;
      font-size: 12px;
    }
  }

  .second-row {
    display: flex;
    ::v-deep.v-text-field {
      margin: 0 24px;
      padding-top: 0;
      font-size: 12px;
    }

    .osi-selector {
      max-width: 350px;
    }

    .filter-controls-spacer {
      min-height: 52px;
    }
  }

  ::v-deep.v-date-range {
    .v-text-field {
      min-width: 250px !important;
    }
  }
}

.pacing-summary-cards {
  .v-card.cursor-pointer {
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .summary-active {
    background-color: #055289 !important;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4) !important;
    transform: translateY(-2px);
  }
}
</style>
